import React, { useState, useEffect } from 'react';

// material-ui imports
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Badge from '@mui/material/Badge';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { dates } from 'api';
// import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import EventIcon from '@mui/icons-material/Event';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';

// Custom component for rendering days with highlighting
function ServerDay(props) {
  const { highlightedDays = [], selectedDate, day, outsideCurrentMonth, ...other } = props;

  const isHighlighted =
    !outsideCurrentMonth &&
    highlightedDays.some((highlightedDate) => {
      return (
        highlightedDate.getDate() === day.getDate() &&
        highlightedDate.getMonth() === day.getMonth() &&
        highlightedDate.getFullYear() === day.getFullYear()
      );
    });

  const isSelected =
    selectedDate &&
    selectedDate.getDate() === day.getDate() &&
    selectedDate.getMonth() === day.getMonth() &&
    selectedDate.getFullYear() === day.getFullYear();

  const showDot = isHighlighted && !isSelected;

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      sx={showDot ? { margin: '2px', borderRadius: '50%' } : undefined}
      badgeContent={showDot ? '' : undefined}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      variant="dot"
      color={showDot ? 'primary' : undefined}
    >
      <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
    </Badge>
  );
}

function DatePickerComp() {
  const [calender, setCalender] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const storeId = JSON.parse(localStorage.getItem('analysisStoreId'));
  const userId = JSON.parse(localStorage.getItem('userData')).id;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.customization.selectedDate);

  // Fetch events data when component mounts or storeId/userId changes
  useEffect(() => {
    async function getEventsData() {
      console.log('Starting getEventsData function');
      const body = {
        store_oid: storeId,
        user_id: userId,
        currnetyear: new Date().getFullYear()
      };
      console.log('API Request Body:', body);

      try {
        console.log('Making API call...');
        const response = await dates(body);
        console.log('Raw API Response:', response);

        if (!response) {
          console.error('No response received from API');
          return;
        }

        if (response.data.code === 200 && Array.isArray(response.data.data)) {
          const eventDates = response.data.data.map((item) => new Date(item.timestamp));
          setEvents(eventDates);
        } else {
          console.error('Invalid response format:', response);
        }
      } catch (error) {
        console.error('Error in getEventsData:', error);
      }
    }

    if (storeId && userId) {
      getEventsData();
    }
  }, [storeId, userId]);

  // Update highlighted days when events change or calendar month changes
  useEffect(() => {
    console.log('Current events:', events);
    if (events.length > 0) {
      const daysToHighlight = events.filter((eventDate) => {
        const isSameMonth = eventDate.getMonth() === calender.getMonth();
        const isSameYear = eventDate.getFullYear() === calender.getFullYear();
        return isSameMonth && isSameYear;
      });
      console.log('Days to highlight:', daysToHighlight);
      setHighlightedDays(daysToHighlight);
    }
  }, [events, calender]);

  // Helper function to update highlighted days for a given month
  const updateHighlightedDays = (date) => {
    if (!events.length) {
      console.log('No events to highlight');
      return;
    }

    const daysToHighlight = events.filter((eventDate) => {
      const isSameMonth = eventDate.getMonth() === date.getMonth();
      const isSameYear = eventDate.getFullYear() === date.getFullYear();
      return isSameMonth && isSameYear;
    });
    console.log('Days to highlight:', daysToHighlight);
    setHighlightedDays(daysToHighlight);
  };

  // Handler for when the calendar month changes
  const handleMonthChange = (date) => {
    updateHighlightedDays(date);
  };

  // Handler for calendar opening
  function handleCalOpen() {
    setOpenDatePicker(true);
    updateHighlightedDays(calender);
  }

  // Handler for date selection
  const handlechange = (date) => {
    setCalender(date);
  };

  // Update redux state when calendar date changes
  useEffect(() => {
    dispatch({
      type: 'selectedDate',
      payload: calender
    });
  }, [calender, dispatch]);

  return (
    <>
      <div className={`flex items-center ${isSmallScreen && 'hidden'}`}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            className="cursor-pointer"
            sx={{
              bgcolor: 'white',
              '& .MuiInputBase-root': {
                height: '40px'
              }
            }}
            slots={{
              day: ServerDay
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                bgcolor: 'white'
              },
              day: {
                highlightedDays,
                selectedDate
              }
            }}
            format="dd/MM/yyyy"
            id="date-picker-inline"
            label="Date Selected"
            value={selectedDate}
            onChange={handlechange}
            onMonthChange={handleMonthChange}
            minDate={new Date(2023, 11, 26)}
            maxDate={new Date()}
            closeOnSelect={false}
            onOpen={handleCalOpen}
            onClose={() => setOpenDatePicker(false)}
            open={openDatePicker}
          />
        </LocalizationProvider>
      </div>
      {isSmallScreen && (
        <IconButton
          sx={{ color: 'black' }}
          onClick={() => {
            setOpenDatePicker(!openDatePicker);
          }}
        >
          <EventIcon />
        </IconButton>
      )}
    </>
  );
}

export default DatePickerComp;
